import React, { useState, useEffect } from 'react';
import { dashboardAPI, forwardingAPI, dealsAPI, tasksAPI } from '../services/api';
import { FiPhone, FiPhoneIncoming, FiPhoneMissed, FiUsers, FiClock, FiActivity, FiPhoneForwarded, FiDollarSign, FiCheckSquare, FiAlertCircle } from 'react-icons/fi';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { formatDuration, formatDateTime, getStatusLabel, getStatusColor, getDirectionLabel, getTrunkStatusLabel } from '../utils/formatters';

export default function DashboardPage() {
  const [data, setData] = useState(null);
  const [forwarding, setForwarding] = useState(null);
  const [crm, setCrm] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadDashboard();
    const interval = setInterval(loadDashboard, 30000);
    return () => clearInterval(interval);
  }, []);

  const loadDashboard = async () => {
    try {
      const [res, fwd, deals, tasks] = await Promise.all([
        dashboardAPI.get(),
        forwardingAPI.status().catch(() => null),
        dealsAPI.stats().catch(() => null),
        tasksAPI.summary().catch(() => null),
      ]);
      setData(res.data);
      if (fwd) setForwarding(fwd.data);
      if (deals || tasks) setCrm({ deals: deals?.data?.stats || null, tasks: tasks?.data?.summary || null });
    } catch (err) {
      console.error('Dashboard error:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <div className="loading-center"><div className="spinner" /></div>;
  if (!data) return <div className="empty-state"><p>Ma'lumot yuklanmadi</p></div>;

  const pieData = [
    { name: 'Javob berildi', value: data.today.answered, color: '#10b981' },
    { name: 'Javobsiz', value: data.today.missed, color: '#ef4444' },
  ].filter(d => d.value > 0);

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Bosh sahifa</h1>
          <p className="page-subtitle">Bugungi umumiy ko'rsatkichlar</p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span className={`badge ${data.system.trunk_status === 'registered' ? 'badge-success' : 'badge-danger'}`}>
            <span className="badge-dot" />
            SIP: {getTrunkStatusLabel(data.system.trunk_status)}
          </span>
        </div>
      </div>

      {/* Call-forwarding status widget */}
      {forwarding && (
        <div className="card" style={{ marginBottom: '20px', borderLeft: `4px solid ${forwarding.active ? '#f59e0b' : '#10b981'}` }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '14px', padding: '4px' }}>
            <div style={{ width: '44px', height: '44px', borderRadius: '12px',
              background: forwarding.active ? '#f59e0b20' : '#10b98120',
              display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <FiPhoneForwarded size={22} color={forwarding.active ? '#f59e0b' : '#10b981'} />
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: '13px', color: 'var(--text-muted)' }}>Qo'ng'iroqlarni yo'naltirish</div>
              {forwarding.active ? (
                <div style={{ fontSize: '15px', fontWeight: '700', color: 'var(--text-primary)' }}>
                  FAOL — «{forwarding.rule.name}» → {forwarding.rule.destination}
                  <span style={{ fontSize: '12px', fontWeight: 500, color: 'var(--text-muted)', marginLeft: 8 }}>
                    {formatDateTime(forwarding.rule.start_at)} — {formatDateTime(forwarding.rule.end_at)}
                  </span>
                </div>
              ) : (
                <div style={{ fontSize: '15px', fontWeight: '700', color: 'var(--text-secondary)' }}>NOFAOL — normal oqim</div>
              )}
            </div>
            <span className={`badge ${forwarding.active ? 'badge-warning' : 'badge-success'}`}>
              <span className="badge-dot" />{forwarding.active ? 'Active' : 'Inactive'}
            </span>
          </div>
        </div>
      )}

      {/* Stats Grid */}
      <div className="stats-grid">
        <StatCard icon={FiPhone} label="Bugungi qo'ng'iroqlar" value={data.today.total} color="#3b82f6" />
        <StatCard icon={FiPhoneIncoming} label="Javob berildi" value={data.today.answered}
          sub={`${data.today.answer_rate}% javob darajasi`} color="#10b981" />
        <StatCard icon={FiPhoneMissed} label="Javobsiz" value={data.today.missed} color="#ef4444" />
        <StatCard icon={FiClock} label="O'rtacha davomiylik" value={formatDuration(data.today.avg_duration)} color="#8b5cf6" />
        <StatCard icon={FiUsers} label="Faol operatorlar" value={`${data.operators.active}/${data.operators.total}`} color="#f59e0b" />
        <StatCard icon={FiActivity} label="Bu oyda" value={data.month.total} sub={`Bu hafta: ${data.week.total}`} color="#06b6d4" />
      </div>

      {/* CRM widgets */}
      {crm && (crm.deals || crm.tasks) && (
        <div className="stats-grid" style={{ marginBottom: 24 }}>
          {crm.deals && (
            <StatCard icon={FiDollarSign} label="Ochiq bitimlar"
              value={`${crm.deals.open_count} ta`}
              sub={`${Number(crm.deals.open_value || 0).toLocaleString('uz-UZ')} UZS`} color="#3b82f6" />
          )}
          {crm.deals && (
            <StatCard icon={FiActivity} label="Bu oy yutilgan"
              value={`${crm.deals.won_month_count} ta`}
              sub={`${Number(crm.deals.won_month_value || 0).toLocaleString('uz-UZ')} UZS · ${crm.deals.conversion_rate}%`} color="#10b981" />
          )}
          {crm.tasks && (
            <StatCard icon={FiCheckSquare} label="Bugungi vazifalar" value={crm.tasks.today || 0} color="#8b5cf6" />
          )}
          {crm.tasks && (
            <StatCard icon={FiAlertCircle} label="Kechikkan vazifalar" value={crm.tasks.overdue || 0} color="#ef4444" />
          )}
        </div>
      )}

      <div className="adm-g-2-1" style={{ gap: '20px', marginBottom: '24px' }}>
        {/* Recent Calls */}
        <div className="card">
          <div className="card-header">
            <span className="card-title">So'nggi qo'ng'iroqlar</span>
          </div>
          <div className="table-wrapper">
            <table>
              <thead>
                <tr>
                  <th>Vaqt</th>
                  <th>Raqam</th>
                  <th>Operator</th>
                  <th>Davomiylik</th>
                  <th>Holat</th>
                </tr>
              </thead>
              <tbody>
                {data.recent_calls.length === 0 ? (
                  <tr><td colSpan="5" style={{ textAlign: 'center', padding: '40px', color: 'var(--text-muted)' }}>Hozircha qo'ng'iroqlar yo'q</td></tr>
                ) : data.recent_calls.map((call, i) => (
                  <tr key={i}>
                    <td style={{ whiteSpace: 'nowrap' }}>{formatDateTime(call.call_date)}</td>
                    <td style={{ fontWeight: '600', color: 'var(--text-primary)' }}>
                      {call.contact_name || (call.direction === 'inbound' ? call.caller_number : call.called_number)}
                      <span style={{ fontSize: '11px', color: 'var(--text-muted)', marginLeft: '6px' }}>
                        {getDirectionLabel(call.direction)}
                      </span>
                    </td>
                    <td>{call.operator_name || '—'}</td>
                    <td>{formatDuration(call.billable_sec || call.duration)}</td>
                    <td>
                      <span className={`badge ${call.status === 'answered' ? 'badge-success' : call.status === 'busy' ? 'badge-warning' : 'badge-danger'}`}>
                        <span className="badge-dot" />
                        {getStatusLabel(call.status)}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Pie Chart */}
        <div className="card">
          <div className="card-header">
            <span className="card-title">Bugungi nisbat</span>
          </div>
          {pieData.length > 0 ? (
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie data={pieData} cx="50%" cy="50%" innerRadius={55} outerRadius={85}
                  paddingAngle={4} dataKey="value" stroke="none">
                  {pieData.map((entry, idx) => <Cell key={idx} fill={entry.color} />)}
                </Pie>
                <Tooltip
                  contentStyle={{ background: '#1e293b', border: '1px solid #2d3a4f', borderRadius: '8px', color: '#f1f5f9' }}
                  formatter={(value, name) => [value, name]}
                />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div style={{ textAlign: 'center', padding: '60px 0', color: 'var(--text-muted)' }}>
              Ma'lumot yo'q
            </div>
          )}
          <div style={{ display: 'flex', justifyContent: 'center', gap: '20px', marginTop: '8px' }}>
            {pieData.map((d, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', color: 'var(--text-secondary)' }}>
                <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: d.color }} />
                {d.name}: {d.value}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, sub, color }) {
  return (
    <div className="stat-card">
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <span className="stat-label">{label}</span>
        <div style={{ width: '36px', height: '36px', borderRadius: '10px', background: `${color}20`,
          display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Icon size={18} color={color} />
        </div>
      </div>
      <span className="stat-value">{value}</span>
      {sub && <span className="stat-sub">{sub}</span>}
    </div>
  );
}
